// ============================================================================
// TRIAL BALANCE TABLE (Sprint 2.10 - Net Income Footer)
// ============================================================================
// Features:
// - Column resizing via drag handles (enforces minWidth)
// - Column reordering via drag-and-drop headers
// - Cell selection and copy (Sprint 2.8)
// - Filtering and hierarchy (Sprint 2.7)
// - Net Income Footer (Sprint 2.10)
//
// Per Phase 2 Description:
// - Column layout persistent per engagement during session
// - Does NOT reset on 'Reset' button click
// - Reverts to defaults only on browser page refresh

import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import type { TableComponents } from 'react-virtuoso';
import { ChevronRight, ChevronDown, Search, X, Filter, GripVertical, RotateCcw, SlidersHorizontal } from 'lucide-react';
import ColumnVisibilityPanel from './components/ColumnVisibilityPanel';
import type { TBAccount, FilterState, FilterMatchType, NumericMatchType, ColumnConfig, ColumnId, JournalEntryHeader } from './types';
import { displayAmountRounded } from './utils/format';
import { DEFAULT_FILTER_STATE } from './types';
import { DEFAULT_COLUMNS} from './columnConfig';
import { 
  createCellKey} from './types/cellSelection';
import NetIncomeFooter from './components/NetIncomeFooter';
import type { NetIncomeValues } from './components/NetIncomeFooter';
import {
  filterAccounts,
  buildHierarchy,
} from './utils/tableLogic';
import type { SubtotalData, RowType, TableRow, ViewState } from './utils/tableLogic';

// Columns that remain pinned to the left during horizontal scrolling
const LOCKED_COLUMN_IDS = new Set<ColumnId>(['select']);

// ============================================================================
// VIEW STATE INTERFACE
// ============================================================================


interface TrialBalanceTableProps {
  accounts: TBAccount[];
  viewState: ViewState;
  selectedAccountIds: Set<string>;  // For bulk action checkboxes
  onSelectionChange: (newSelection: Set<string>) => void;
  onRowClick: (account: TBAccount) => void;
  onRowDoubleClick: (account: TBAccount) => void;
  // Cell selection callbacks
  onCellSelectionChange?: (summary: { count: number; sum: number }) => void;
  // Sprint 2.10: Net Income Footer props
  netIncome: NetIncomeValues;
  selectionCount: number;
  selectionSum: number;
  onOpenJournalEntry: (headerId: string) => void;
  onOpenMAdjustment: (headerId: string) => void;
  // Sprint 5.1: M-Adj Ref column
  mAdjHeaders?: JournalEntryHeader[];
  onUpdateMAdjRef?: (accountId: string, mAdjRef: string) => void;
  // Toolbar controls lifted from Workspace
  entityCount: number;
  onExpandCollapse: () => void;
  onToggleEntities: () => void;
  onToggleFSRef: () => void;
  onToggleGroupings: () => void;
  onToggleSubGroupings: () => void;
  onReset: () => void;
  onColumnPanelOpenChange?: (isOpen: boolean) => void;
}

// ============================================================================
// TABLE ROW TYPES — imported from ./utils/tableLogic.ts
// Re-export so existing consumers can still import from this file if needed.
// ============================================================================
export type { SubtotalData, RowType, TableRow };

// ============================================================================
// CELL SELECTION STATE
// ============================================================================

interface CellPosition {
  rowId: string;
  columnId: string;
}

interface CellSelectionState {
  selectedCells: Set<string>;
  anchorCell: CellPosition | null;
  isDragging: boolean;
}

// ============================================================================
// COLUMN MANAGEMENT STATE (Sprint 2.9)
// ============================================================================

interface ResizeState {
  isResizing: boolean;
  columnId: ColumnId | null;
  startX: number;
  startWidth: number;
}

interface DragState {
  isDragging: boolean;
  columnId: ColumnId | null;
  columnIndex: number;
  dropTargetIndex: number | null;
}

// ============================================================================
// FILTERING & HIERARCHY — extracted to ./utils/tableLogic.ts
// Functions: matchesTextFilter, matchesNumericFilter, filterAccounts,
//            calculateSubtotal, buildHierarchy
// ============================================================================

// ============================================================================
// FILTER ROW COMPONENT
// ============================================================================

interface FilterRowProps {
  filters: FilterState;
  onFilterChange: (key: keyof FilterState, value: string) => void;
  onMatchTypeChange: (key: keyof FilterState, matchType: FilterMatchType | NumericMatchType) => void;
  onClearFilters: () => void;
  uniqueGroupings: string[];
  hasActiveFilters: boolean;
  columns: ColumnConfig[];
  stickyOffsets?: Partial<Record<ColumnId, number>>;
  lastLockedColumnId?: ColumnId | null;
}

const FilterRow: React.FC<FilterRowProps> = ({
  filters,
  onFilterChange,
  onMatchTypeChange,
  onClearFilters,
  uniqueGroupings,
  hasActiveFilters,
  columns,
  stickyOffsets,
  lastLockedColumnId
}) => {
  const [showMatchTypeDropdown, setShowMatchTypeDropdown] = useState<keyof FilterState | null>(null);
  
  const textMatchOptions: { value: FilterMatchType; label: string }[] = [
    { value: 'contains', label: 'Contains' },
    { value: 'equals', label: 'Equals' },
    { value: 'startsWith', label: 'Starts with' },
    { value: 'notContains', label: 'Not contains' },
    { value: 'notEquals', label: 'Not equals' },
  ];
  
  const numericMatchOptions: { value: NumericMatchType; label: string }[] = [
    { value: 'equals', label: '=' },
    { value: 'greaterThan', label: '>' },
    { value: 'lessThan', label: '<' },
    { value: 'greaterOrEqual', label: '>=' },
    { value: 'lessOrEqual', label: '<=' },
    { value: 'notEquals', label: '≠' },
  ];
  
  const renderTextFilter = (key: keyof FilterState, placeholder: string) => (
    <div className="relative flex items-center gap-1">
      <div className="relative flex-1">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
        <input
          type="text"
          value={filters[key].value}
          onChange={(e) => onFilterChange(key, e.target.value)}
          placeholder={placeholder}
          className="w-full pl-7 pr-2 py-1 text-xs rounded border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-green-500"
        />
      </div>
      <div className="relative">
        <button
          onClick={() => setShowMatchTypeDropdown(showMatchTypeDropdown === key ? null : key)}
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          title="Filter type"
        >
          <Filter className="w-3 h-3" />
        </button>
        {showMatchTypeDropdown === key && (
          <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 rounded shadow-lg py-1 min-w-[120px]">
            {textMatchOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => {
                  onMatchTypeChange(key, opt.value);
                  setShowMatchTypeDropdown(null);
                }}
                className={`w-full px-3 py-1 text-left text-xs hover:bg-gray-100 dark:hover:bg-neutral-700 ${
                  filters[key].matchType === opt.value ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
  
  const renderNumericFilter = (key: keyof FilterState, placeholder: string) => (
    <div className="relative flex items-center gap-1">
      <select
        value={filters[key].matchType}
        onChange={(e) => onMatchTypeChange(key, e.target.value as NumericMatchType)}
        className="w-10 py-1 text-xs rounded border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-green-500"
      >
        {numericMatchOptions.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <input
        type="text"
        value={filters[key].value}
        onChange={(e) => onFilterChange(key, e.target.value)}
        placeholder={placeholder}
        className="flex-1 px-2 py-1 text-xs rounded border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-green-500 text-right font-mono"
      />
    </div>
  );
  
  const renderGroupingDropdown = () => (
    <select
      value={filters.grouping.value}
      onChange={(e) => onFilterChange('grouping', e.target.value)}
      className="w-full px-2 py-1 text-xs rounded border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-green-500"
    >
      <option value="">All</option>
      {uniqueGroupings.map(g => (
        <option key={g} value={g}>{g}</option>
      ))}
    </select>
  );

  // Render filter cell based on column ID
  const renderFilterCell = (columnId: ColumnId, width: number) => {
    const baseStyle = { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` };
    const stickyLeft = stickyOffsets?.[columnId];
    const stickyProps: React.CSSProperties = stickyLeft !== undefined
      ? { position: 'sticky', left: `${stickyLeft}px`, zIndex: 20 }
      : {};
    const style = { ...baseStyle, ...stickyProps };
    const lockedBg = stickyLeft !== undefined ? 'bg-gray-50 dark:bg-neutral-800' : '';
    const lockedBorder = columnId === lastLockedColumnId ? 'border-r-2 border-gray-300 dark:border-neutral-600' : '';

    switch (columnId) {
      case 'select':
        return (
          <td key={columnId} className={`px-2 py-1 text-center ${lockedBg} ${lockedBorder}`} style={style}>
            {hasActiveFilters && (
              <button
                onClick={onClearFilters}
                className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                title="Clear all filters"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </td>
        );
      case 'accountNumber':
        return <td key={columnId} className={`px-2 py-1 ${lockedBg} ${lockedBorder}`} style={style}>{renderTextFilter('accountNumber', 'Filter...')}</td>;
      case 'accountName':
        return <td key={columnId} className={`px-2 py-1 ${lockedBg} ${lockedBorder}`} style={style}>{renderTextFilter('accountName', 'Filter...')}</td>;
      case 'bookBalance':
        return <td key={columnId} className="px-2 py-1" style={style}>{renderNumericFilter('bookBalance', '0')}</td>;
      case 'jeNumber':
        return <td key={columnId} className="px-2 py-1" style={style}></td>;
      case 'rje':
        return <td key={columnId} className="px-2 py-1" style={style}>{renderNumericFilter('rje', '0')}</td>;
      case 'aje':
        return <td key={columnId} className="px-2 py-1" style={style}>{renderNumericFilter('aje', '0')}</td>;
      case 'adjBook':
        return <td key={columnId} className="px-2 py-1" style={style}>{renderNumericFilter('adjBook', '0')}</td>;
      case 'mAdjNumber':
        return <td key={columnId} className="px-2 py-1" style={style}></td>;
      case 'mAdj':
        return <td key={columnId} className="px-2 py-1" style={style}>{renderNumericFilter('mAdj', '0')}</td>;
      case 'ftax':
        return <td key={columnId} className="px-2 py-1" style={style}>{renderNumericFilter('ftax', '0')}</td>;
      case 'grouping':
        return <td key={columnId} className="px-2 py-1" style={style}>{renderGroupingDropdown()}</td>;
      case 'subGrouping':
        return <td key={columnId} className="px-2 py-1" style={style}>{renderTextFilter('subGrouping', 'Filter...')}</td>;
      case 'mAdjRef':
      case 'pyAdjBook':
      case 'pyMAdj':
      case 'pyFtax':
        return <td key={columnId} className="px-2 py-1" style={style}></td>;
      default:
        return <td key={columnId} className="px-2 py-1" style={style}></td>;
    }
  };
  
  return (
    <tr className="bg-gray-50 dark:bg-neutral-800 border-b border-gray-200 dark:border-neutral-700">
      {columns.filter(col => col.visible).map(col => renderFilterCell(col.id, col.width))}
    </tr>
  );
};

// ============================================================================
// COLUMN HEADER COMPONENT (Sprint 2.9)
// ============================================================================

interface ColumnHeaderProps {
  column: ColumnConfig;
  index: number;
  isResizing: boolean;
  isDragging: boolean;
  isDropTarget: boolean;
  onResizeStart: (e: React.MouseEvent, columnId: ColumnId, startWidth: number) => void;
  onDragStart: (e: React.DragEvent, columnId: ColumnId, index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  masterCheckboxState?: { checked: boolean; indeterminate: boolean };
  onMasterCheckboxClick?: () => void;
  stickyLeft?: number;
  isLastLocked?: boolean;
}

const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  column,
  index,
  isResizing,
  isDragging,
  isDropTarget,
  onResizeStart,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  masterCheckboxState,
  onMasterCheckboxClick,
  stickyLeft,
  isLastLocked
}) => {
  // Determine text alignment based on column type
  const getTextAlign = () => {
    if (column.isCurrency) return 'text-right';
    if (column.id === 'select' || column.id === 'jeNumber' || column.id === 'mAdjNumber') return 'text-center';
    return 'text-left';
  };

  const stickyStyle: React.CSSProperties = stickyLeft !== undefined
    ? { position: 'sticky', left: `${stickyLeft}px`, top: 0, zIndex: 40 }
    : {};
  const lockedBorderClass = isLastLocked ? 'border-r-2 border-gray-300 dark:border-neutral-600' : '';

  // Special case for select column
  if (column.id === 'select') {
    return (
      <th
        className={`px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 text-center bg-gray-100 dark:bg-neutral-700 sticky-corner-th ${lockedBorderClass} ${
          isDropTarget ? 'ring-2 ring-inset ring-blue-400' : ''
        }`}
        style={{
          width: `${column.width}px`,
          minWidth: `${column.minWidth}px`,
          maxWidth: `${column.width}px`,
          ...stickyStyle
        }}
        onDragOver={(e) => onDragOver(e, index)}
        onDrop={(e) => onDrop(e, index)}
      >
        <input
          type="checkbox"
          checked={masterCheckboxState?.checked ?? false}
          ref={el => {
            if (el) el.indeterminate = masterCheckboxState?.indeterminate ?? false;
          }}
          onChange={onMasterCheckboxClick}
          className="w-4 h-4 rounded border-gray-300 dark:border-neutral-600 text-green-600 focus:ring-green-500 cursor-pointer"
          title="Select/Deselect all visible accounts"
        />
      </th>
    );
  }

  return (
    <th
      className={`px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 ${getTextAlign()} bg-gray-100 dark:bg-neutral-700 relative group select-none ${lockedBorderClass} ${
        isDragging ? 'opacity-50' : ''
      } ${isDropTarget ? 'ring-2 ring-inset ring-blue-400 bg-blue-50 dark:bg-blue-900/30' : ''}`}
      style={{
        width: `${column.width}px`,
        minWidth: `${column.minWidth}px`,
        maxWidth: `${column.width}px`,
        ...stickyStyle
      }}
      draggable
      onDragStart={(e) => onDragStart(e, column.id, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragEnd={onDragEnd}
      onDrop={(e) => onDrop(e, index)}
    >
      <div className="flex items-center gap-1">
        {/* Drag handle indicator */}
        <GripVertical className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab flex-shrink-0" />
        
        {/* Column label */}
        <span className="truncate flex-1">{column.label}</span>
      </div>
      
      {/* Resize handle */}
      <div
        className={`absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 transition-colors ${
          isResizing ? 'bg-blue-500' : 'bg-gray-300 dark:bg-neutral-500'
        }`}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onResizeStart(e, column.id, column.width);
        }}
        style={{ touchAction: 'none' }}
      />
    </th>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function TrialBalanceTable({
  accounts,
  viewState,
  selectedAccountIds,
  onSelectionChange,
  onRowClick,
  onRowDoubleClick,
  onCellSelectionChange,
  netIncome,
  selectionCount,
  selectionSum,
  onOpenJournalEntry,
  onOpenMAdjustment,
  mAdjHeaders,
  onUpdateMAdjRef,
  entityCount,
  onExpandCollapse,
  onToggleEntities,
  onToggleFSRef,
  onToggleGroupings,
  onToggleSubGroupings,
  onReset,
  onColumnPanelOpenChange,
}: TrialBalanceTableProps) {

  // State for expanded/collapsed rows
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // State for filters
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTER_STATE);
  const [showFilters, setShowFilters] = useState(false);

  // State for multi-JE popover
  const [openPopoverAccountId, setOpenPopoverAccountId] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // State for multi-M-Adj popover
  const [openMAdjPopoverAccountId, setOpenMAdjPopoverAccountId] = useState<string | null>(null);
  const mAdjPopoverRef = useRef<HTMLDivElement>(null);
  
  // State for cell selection (Sprint 2.8)
  const [cellSelection, setCellSelection] = useState<CellSelectionState>({
    selectedCells: new Set<string>(),
    anchorCell: null,
    isDragging: false
  });
  
  // ============================================================================
  // COLUMN MANAGEMENT STATE (Sprint 2.9)
  // ============================================================================
  
  // Column configuration - persists during session, resets on page refresh
  const [columns, setColumns] = useState<ColumnConfig[]>(() => 
    DEFAULT_COLUMNS.map(col => ({ ...col }))
  );
  
  // Resize state
  const [resizeState, setResizeState] = useState<ResizeState>({
    isResizing: false,
    columnId: null,
    startX: 0,
    startWidth: 0
  });
  
  // Drag state for column reordering
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    columnId: null,
    columnIndex: -1,
    dropTargetIndex: null
  });

  // Column visibility panel state
  const [isColumnPanelOpen, setIsColumnPanelOpen] = useState(false);

  const handleToggleVisibility = useCallback((columnId: ColumnId) => {
    setColumns((prev) =>
      prev.map((col) => col.id === columnId ? { ...col, visible: !col.visible } : col)
    );
  }, []);
  
  // Ref for table container (for keyboard event handling)
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Close multi-JE popover on click outside
  useEffect(() => {
    if (!openPopoverAccountId) return;
    const close = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpenPopoverAccountId(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openPopoverAccountId]);

  // Close multi-M-Adj popover on click outside
  useEffect(() => {
    if (!openMAdjPopoverAccountId) return;
    const close = (e: MouseEvent) => {
      if (mAdjPopoverRef.current && !mAdjPopoverRef.current.contains(e.target as Node)) {
        setOpenMAdjPopoverAccountId(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openMAdjPopoverAccountId]);

  // Close the column visibility panel when the bulk action panel activates
  useEffect(() => {
    if (selectedAccountIds.size > 0 && isColumnPanelOpen) {
      setIsColumnPanelOpen(false);
      onColumnPanelOpenChange?.(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountIds]);

  // Calculate total table width
  const totalTableWidth = useMemo(() => {
    return columns
      .filter(col => col.visible)
      .reduce((sum, col) => sum + col.width, 0);
  }, [columns]);
  
  // Get unique groupings from accounts for dropdown
  const uniqueGroupings = useMemo(() => {
    const groupings = new Set(accounts.map(acc => acc.grouping).filter(Boolean));
    return Array.from(groupings).sort();
  }, [accounts]);
  
  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(f => f.value.trim() !== '');
  }, [filters]);
  
  // Filter accounts first, then build hierarchy
  const filteredAccounts = useMemo(() => {
    if (!hasActiveFilters) return accounts;
    return filterAccounts(accounts, filters);
  }, [accounts, filters, hasActiveFilters]);
  
  // Build hierarchy from filtered accounts
  const allRows = useMemo(() => {
    return buildHierarchy(filteredAccounts, viewState);
  }, [filteredAccounts, viewState]);
  
  // Filter rows based on expansion state
  const visibleRows = useMemo(() => {
    if (viewState.expandAll) {
      return allRows;
    }
    
    if (!viewState.showEntities && !viewState.showFSRef && !viewState.showGroupings && !viewState.showSubGroupings) {
      return allRows;
    }
    
    const visible: TableRow[] = [];
    const shouldShow = (row: TableRow): boolean => {
      if (row.isSubtotal) {
        if (!row.parentId) return true;
        return expandedRows.has(row.parentId);
      } else {
        if (!row.parentId) return true;
        return expandedRows.has(row.parentId);
      }
    };
    
    allRows.forEach(row => {
      if (shouldShow(row)) {
        visible.push(row);
      }
    });
    
    return visible;
  }, [allRows, expandedRows, viewState.expandAll, viewState.showEntities, viewState.showFSRef, viewState.showGroupings, viewState.showSubGroupings]);
  
  // Create accounts map for fast lookup
  const accountsMap = useMemo(() => {
    const map = new Map<string, TBAccount>();
    accounts.forEach(acc => {
      if (acc.id) map.set(acc.id, acc);
    });
    return map;
  }, [accounts]);

  // ============================================================================
  // COLUMN RESIZE HANDLERS (Sprint 2.9)
  // ============================================================================

  const handleResizeStart = useCallback((e: React.MouseEvent, columnId: ColumnId, startWidth: number) => {
    e.preventDefault();
    setResizeState({
      isResizing: true,
      columnId,
      startX: e.clientX,
      startWidth
    });
  }, []);

  useEffect(() => {
    if (!resizeState.isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const column = columns.find(c => c.id === resizeState.columnId);
      if (!column) return;

      const delta = e.clientX - resizeState.startX;
      const newWidth = Math.max(resizeState.startWidth + delta, column.minWidth);

      setColumns(prev => prev.map(col => 
        col.id === resizeState.columnId 
          ? { ...col, width: newWidth }
          : col
      ));
    };

    const handleMouseUp = () => {
      setResizeState({
        isResizing: false,
        columnId: null,
        startX: 0,
        startWidth: 0
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizeState, columns]);

  // ============================================================================
  // COLUMN DRAG & DROP HANDLERS (Sprint 2.9)
  // ============================================================================

  const handleDragStart = useCallback((e: React.DragEvent, columnId: ColumnId, index: number) => {
    // Don't allow dragging the select column
    if (columnId === 'select') {
      e.preventDefault();
      return;
    }
    
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnId);
    
    // Add slight delay for drag image to show properly
    setTimeout(() => {
      setDragState({
        isDragging: true,
        columnId,
        columnIndex: index,
        dropTargetIndex: null
      });
    }, 0);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    setDragState(prev => ({
      ...prev,
      dropTargetIndex: index
    }));
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragState({
      isDragging: false,
      columnId: null,
      columnIndex: -1,
      dropTargetIndex: null
    });
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    const { columnIndex } = dragState;
    
    if (columnIndex === -1 || columnIndex === dropIndex) {
      handleDragEnd();
      return;
    }
    
    // Don't allow dropping before the select column (index 0)
    if (dropIndex === 0) {
      handleDragEnd();
      return;
    }

    setColumns(prev => {
      // Convert visible-column indices to full-array indices so hidden columns
      // are not accidentally displaced when all-visible and drag is active.
      const visibleIndices = prev.reduce<number[]>((acc, col, i) => {
        if (col.visible) acc.push(i);
        return acc;
      }, []);

      const fullDragIdx = visibleIndices[columnIndex];
      const fullDropIdx = visibleIndices[dropIndex];
      if (fullDragIdx === undefined || fullDropIdx === undefined) return prev;

      const next = [...prev];
      const [moved] = next.splice(fullDragIdx, 1);
      const adjustedDrop = fullDropIdx > fullDragIdx ? fullDropIdx - 1 : fullDropIdx;
      next.splice(adjustedDrop, 0, moved);
      return next;
    });
    
    handleDragEnd();
  }, [dragState, handleDragEnd]);

  // Calculate cell selection summary and notify parent
  useEffect(() => {
    if (!onCellSelectionChange) return;
    
    let count = 0;
    let sum = 0;
    
    const visibleAccountIds = new Set(
      visibleRows.filter(row => !row.isSubtotal).map(row => row.id)
    );
    
    cellSelection.selectedCells.forEach(cellKey => {
      const [rowId, columnId] = cellKey.split(':');
      if (!visibleAccountIds.has(rowId)) return;
      
      const account = accountsMap.get(rowId);
      if (!account) return;
      
      // Get numeric value for this cell
      const numValue = getNumericValue(account, columnId);
      if (numValue !== null) {
        count++;
        sum += numValue;
      }
    });
    
    onCellSelectionChange({ count, sum });
  }, [cellSelection.selectedCells, visibleRows, accountsMap, onCellSelectionChange]);
  
  // Handle filter changes
  const handleFilterChange = useCallback((key: keyof FilterState, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: { ...prev[key], value, isActive: value.trim() !== '' }
    }));
  }, []);
  
  const handleMatchTypeChange = useCallback((key: keyof FilterState, matchType: FilterMatchType | NumericMatchType) => {
    setFilters(prev => ({
      ...prev,
      [key]: { ...prev[key], matchType }
    }));
  }, []);
  
  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTER_STATE);
  }, []);
  
  // Toggle row expansion
  const handleToggleRow = useCallback((rowId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);
  
  // Master checkbox handlers
  // REFACTORED (Sprint UI/UX): Selects ALL accounts in filteredAccounts,
  // not just visibleRows, so collapsed groupings are included in bulk actions.
  const handleMasterCheckboxClick = useCallback(() => {
    const allFilteredIds = filteredAccounts
      .map(acc => acc.id!)
      .filter(Boolean);
    
    const allSelected = allFilteredIds.length > 0 &&
      allFilteredIds.every(id => selectedAccountIds.has(id));
    
    const newSelection = new Set(selectedAccountIds);
    if (allSelected) {
      // Deselect all filtered accounts
      allFilteredIds.forEach(id => newSelection.delete(id));
    } else {
      // Select all filtered accounts (including those in collapsed groups)
      allFilteredIds.forEach(id => newSelection.add(id));
    }
    
    onSelectionChange(newSelection);
  }, [filteredAccounts, selectedAccountIds, onSelectionChange]);
  
  const masterCheckboxState = useMemo(() => {
    const allFilteredIds = filteredAccounts
      .map(acc => acc.id!)
      .filter(Boolean);
    
    if (allFilteredIds.length === 0) {
      return { checked: false, indeterminate: false };
    }
    
    const selectedCount = allFilteredIds.filter(id => selectedAccountIds.has(id)).length;
    
    if (selectedCount === 0) {
      return { checked: false, indeterminate: false };
    } else if (selectedCount === allFilteredIds.length) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  }, [filteredAccounts, selectedAccountIds]);
  
  const handleCheckboxClick = useCallback((e: React.MouseEvent, accountId: string) => {
    e.stopPropagation();
    
    const newSelection = new Set(selectedAccountIds);
    if (newSelection.has(accountId)) {
      newSelection.delete(accountId);
    } else {
      newSelection.add(accountId);
    }
    onSelectionChange(newSelection);
  }, [selectedAccountIds, onSelectionChange]);

  // ============================================================================
  // CELL SELECTION HANDLERS (Sprint 2.8)
  // ============================================================================
  
  const handleCellClick = useCallback((
    rowId: string,
    columnId: string,
    rowIndex: number,
    e: React.MouseEvent
  ) => {
    // Don't select cells in subtotal rows
    const row = visibleRows[rowIndex];
    if (row?.isSubtotal) return;
    
    const cellKey = createCellKey(rowId, columnId);
    const clickedPosition: CellPosition = { rowId, columnId };
    
    setCellSelection(prev => {
      // Ctrl/Cmd + Click: Toggle individual cell
      if (e.ctrlKey || e.metaKey) {
        const newSelected = new Set(prev.selectedCells);
        if (newSelected.has(cellKey)) {
          newSelected.delete(cellKey);
        } else {
          newSelected.add(cellKey);
        }
        return {
          ...prev,
          selectedCells: newSelected,
          anchorCell: clickedPosition
        };
      }
      
      // Shift + Click: Range selection from anchor
      if (e.shiftKey && prev.anchorCell) {
        const newSelected = selectCellRange(prev.anchorCell, clickedPosition, visibleRows, columns);
        return {
          ...prev,
          selectedCells: newSelected
        };
      }
      
      // Regular click: Select single cell, set new anchor
      return {
        ...prev,
        selectedCells: new Set([cellKey]),
        anchorCell: clickedPosition
      };
    });
  }, [visibleRows, columns]);
  
  const handleCellMouseDown = useCallback((
    rowId: string,
    columnId: string,
    rowIndex: number,
    e: React.MouseEvent
  ) => {
    // Only start drag on left mouse button without modifiers
    if (e.button !== 0 || e.shiftKey || e.ctrlKey || e.metaKey) return;
    
    const row = visibleRows[rowIndex];
    if (row?.isSubtotal) return;
    
    const clickedPosition: CellPosition = { rowId, columnId };
    const cellKey = createCellKey(rowId, columnId);
    
    setCellSelection({
      isDragging: true,
      anchorCell: clickedPosition,
      selectedCells: new Set([cellKey])
    });
  }, [visibleRows]);
  
  const handleCellMouseEnter = useCallback((
    rowId: string,
    columnId: string,
    rowIndex: number
  ) => {
    setCellSelection(prev => {
      if (!prev.isDragging || !prev.anchorCell) return prev;
      
      const row = visibleRows[rowIndex];
      if (row?.isSubtotal) return prev;
      
      const currentPosition: CellPosition = { rowId, columnId };
      const newSelected = selectCellRange(prev.anchorCell, currentPosition, visibleRows, columns);
      
      return {
        ...prev,
        selectedCells: newSelected
      };
    });
  }, [visibleRows, columns]);
  
  const handleMouseUp = useCallback(() => {
    setCellSelection(prev => ({
      ...prev,
      isDragging: false
    }));
  }, []);
  
  // Copy selected cells to clipboard
  const copySelectionToClipboard = useCallback(async () => {
    if (cellSelection.selectedCells.size === 0) return;

    // Get selected columns (sorted by current column order)
    const selectedColumnIds = new Set<string>();
    const selectedRowIds = new Set<string>();

    cellSelection.selectedCells.forEach(cellKey => {
      const [rowId, columnId] = cellKey.split(':');
      selectedColumnIds.add(columnId);
      selectedRowIds.add(rowId);
    });

    // Sort columns by their current order in the columns array
    const visibleColumns = columns.filter(col => col.visible);
    const sortedColumns = visibleColumns
      .filter(col => selectedColumnIds.has(col.id))
      .map(col => col.id);

    // Get visible account rows (exclude subtotals)
    const orderedRows = visibleRows
      .filter(row => !row.isSubtotal && selectedRowIds.has(row.id))
      .map(row => row.id);

    if (orderedRows.length === 0 || sortedColumns.length === 0) return;

    // Build header row
    const headers = sortedColumns.map(colId => {
      const col = columns.find(c => c.id === colId);
      return col ? col.label : colId;
    });

    // Build data rows
    const dataRows: string[][] = [];

    for (const rowId of orderedRows) {
      const account = accountsMap.get(rowId);
      if (!account) continue;

      const rowData: string[] = [];
      for (const colId of sortedColumns) {
        const cellKey = createCellKey(rowId, colId);
        if (cellSelection.selectedCells.has(cellKey)) {
          rowData.push(getCellDisplayValueForCopy(account, colId as ColumnId));
        } else {
          rowData.push('');
        }
      }
      dataRows.push(rowData);
    }

    // Build tab-delimited string
    const allRows = [headers, ...dataRows];
    const clipboardText = allRows.map(row => row.join('\t')).join('\n');

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(clipboardText);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [cellSelection.selectedCells, columns, visibleRows, accountsMap]);

  // Keyboard handler for copy (Ctrl+C)
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      // Ctrl+C or Cmd+C to copy
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (cellSelection.selectedCells.size > 0) {
          e.preventDefault();
          await copySelectionToClipboard();
        }
      }

      // Escape to clear selection
      if (e.key === 'Escape') {
        setCellSelection({
          selectedCells: new Set(),
          anchorCell: null,
          isDragging: false
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [cellSelection.selectedCells, handleMouseUp, copySelectionToClipboard]);
  
  // Check if a cell is selected
  const isCellSelected = useCallback((rowId: string, columnId: string): boolean => {
    return cellSelection.selectedCells.has(createCellKey(rowId, columnId));
  }, [cellSelection.selectedCells]);
  
  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderFinancialCell = (
    rowId: string,
    columnId: ColumnId,
    rowIndex: number,
    amount: number,
    width: number,
    isBold: boolean = false,
    isSubtotal: boolean = false
  ) => {
    const { text, isNegative } = displayAmountRounded(amount);
    const isSelected = !isSubtotal && isCellSelected(rowId, columnId);
    
    return (
      <td 
        key={columnId}
        className={`
          px-3 py-2 text-sm font-mono text-right select-none whitespace-nowrap overflow-hidden
          ${isBold ? 'font-semibold' : ''} 
          ${isNegative ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}
          ${isSelected ? 'bg-blue-100 dark:bg-blue-800/40 ring-1 ring-inset ring-blue-400 dark:ring-blue-500' : ''}
          ${!isSubtotal ? 'cursor-cell' : ''}
        `}
        style={{ 
          width: `${width}px`, 
          minWidth: `${width}px`,
          maxWidth: `${width}px`
        }}
        onClick={(e) => !isSubtotal && handleCellClick(rowId, columnId, rowIndex, e)}
        onMouseDown={(e) => !isSubtotal && handleCellMouseDown(rowId, columnId, rowIndex, e)}
        onMouseEnter={() => !isSubtotal && cellSelection.isDragging && handleCellMouseEnter(rowId, columnId, rowIndex)}
      >
        {text}
      </td>
    );
  };
  
  const renderTextCell = (
    rowId: string,
    columnId: ColumnId,
    rowIndex: number,
    value: string,
    width: number,
    isSubtotal: boolean = false,
    className: string = '',
    stickyStyle?: React.CSSProperties,
    stickyBgClass?: string
  ) => {
    const isSelected = !isSubtotal && isCellSelected(rowId, columnId);

    return (
      <td
        key={columnId}
        className={`
          px-3 py-2 text-sm select-none whitespace-nowrap overflow-hidden text-ellipsis
          ${className}
          ${stickyStyle && !isSelected ? (stickyBgClass ?? '') : ''}
          ${isSelected ? 'bg-blue-100 dark:bg-blue-800/40 ring-1 ring-inset ring-blue-400 dark:ring-blue-500' : ''}
          ${!isSubtotal ? 'cursor-cell' : ''}
        `}
        style={{
          width: `${width}px`,
          minWidth: `${width}px`,
          maxWidth: `${width}px`,
          ...(stickyStyle ?? {})
        }}
        onClick={(e) => !isSubtotal && handleCellClick(rowId, columnId, rowIndex, e)}
        onMouseDown={(e) => !isSubtotal && handleCellMouseDown(rowId, columnId, rowIndex, e)}
        onMouseEnter={() => !isSubtotal && cellSelection.isDragging && handleCellMouseEnter(rowId, columnId, rowIndex)}
      >
        {value}
      </td>
    );
  };

  // Render a single cell based on column configuration
  const renderCell = (
    row: TableRow,
    rowIndex: number,
    column: ColumnConfig,
    account: TBAccount
  ) => {
    const { id, width } = column;
    
    switch (id) {
      case 'select': {
        const selectLeft = stickyOffsets['select'];
        const isRowSelected = selectedAccountIds.has(account.id!);
        return (
          <td
            key={id}
            className={`px-3 py-2 text-center ${
              selectLeft !== undefined
                ? isRowSelected
                  ? 'bg-blue-50 dark:bg-blue-900/20'
                  : 'bg-white dark:bg-neutral-800'
                : ''
            } ${id === lastLockedColumnId ? 'border-r-2 border-gray-300 dark:border-neutral-600' : ''}`}
            style={{
              width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px`,
              ...(selectLeft !== undefined ? { position: 'sticky' as const, left: `${selectLeft}px`, zIndex: 20 } : {})
            }}
          >
            <input
              type="checkbox"
              checked={isRowSelected}
              onChange={() => {}}
              onClick={(e) => handleCheckboxClick(e, account.id!)}
              className="w-4 h-4 rounded border-gray-300 dark:border-neutral-600 text-green-600 focus:ring-green-500 cursor-pointer"
            />
          </td>
        );
      }
      case 'accountNumber': {
        const anLeft = stickyOffsets['accountNumber'];
        const isRowSelected = selectedAccountIds.has(account.id!);
        return renderTextCell(
          row.id, id, rowIndex, account.accountNumber, width, false,
          `font-mono text-gray-900 dark:text-gray-100${id === lastLockedColumnId ? ' border-r-2 border-gray-300 dark:border-neutral-600' : ''}`,
          anLeft !== undefined ? { position: 'sticky' as const, left: `${anLeft}px`, zIndex: 20 } : undefined,
          isRowSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-neutral-800'
        );
      }
      case 'accountName': {
        const anLeft = stickyOffsets['accountName'];
        const isRowSelected = selectedAccountIds.has(account.id!);
        return renderTextCell(
          row.id, id, rowIndex, account.accountName, width, false,
          `text-gray-900 dark:text-gray-100${id === lastLockedColumnId ? ' border-r-2 border-gray-300 dark:border-neutral-600' : ''}`,
          anLeft !== undefined ? { position: 'sticky' as const, left: `${anLeft}px`, zIndex: 20 } : undefined,
          isRowSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-neutral-800'
        );
      }
      case 'bookBalance':
        return renderFinancialCell(row.id, id, rowIndex, account.bookBalance, width);
      case 'jeNumber': {
        const relatedJEs = account.relatedJEs ?? [];
        if (relatedJEs.length === 0) {
          return renderTextCell(row.id, id, rowIndex, '-', width, false, 'font-mono text-center text-gray-600 dark:text-gray-400');
        }
        if (relatedJEs.length === 1) {
          return (
            <td
              key={id}
              className="px-3 py-2 text-sm font-mono text-center text-blue-600 dark:text-blue-400 underline cursor-pointer select-none whitespace-nowrap overflow-hidden"
              style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }}
              onClick={(e) => { e.stopPropagation(); onOpenJournalEntry(relatedJEs[0].id); }}
            >
              {relatedJEs[0].jeNumber}
            </td>
          );
        }
        // Multiple JEs — show "Multi" with popover
        const isOpen = openPopoverAccountId === account.id;
        return (
          <td
            key={id}
            className="px-3 py-2 text-sm font-mono text-center select-none whitespace-nowrap overflow-visible"
            style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px`, position: 'relative' }}
            onClick={(e) => e.stopPropagation()}
          >
            <span
              className="text-blue-600 dark:text-blue-400 underline cursor-pointer"
              onClick={(e) => { e.stopPropagation(); setOpenPopoverAccountId(isOpen ? null : account.id!); }}
            >
              Multi
            </span>
            {isOpen && (
              <div
                ref={popoverRef}
                className="absolute left-1/2 -translate-x-1/2 top-full mt-1 bg-white/95 dark:bg-neutral-800/95 backdrop-blur-sm border border-gray-200 dark:border-neutral-600 shadow-lg rounded-lg z-50 min-w-[140px] py-1"
              >
                {relatedJEs.map(je => (
                  <div
                    key={je.id}
                    className="px-3 py-1.5 text-sm font-mono text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-neutral-700 cursor-pointer whitespace-nowrap transition-colors"
                    onClick={(e) => { e.stopPropagation(); setOpenPopoverAccountId(null); onOpenJournalEntry(je.id); }}
                  >
                    {je.jeNumber}
                  </div>
                ))}
              </div>
            )}
          </td>
        );
      }
      case 'rje':
        return renderFinancialCell(row.id, id, rowIndex, account.rje, width);
      case 'aje':
        return renderFinancialCell(row.id, id, rowIndex, account.aje, width);
      case 'adjBook':
        return renderFinancialCell(row.id, id, rowIndex, account.adjBook ?? 0, width, true);
      case 'mAdjNumber': {
        const relatedMAdjs = account.relatedMAdjs ?? [];
        if (relatedMAdjs.length === 0) {
          return renderTextCell(row.id, id, rowIndex, '-', width, false, 'font-mono text-center text-gray-600 dark:text-gray-400');
        }
        if (relatedMAdjs.length === 1) {
          return (
            <td
              key={id}
              className="px-3 py-2 text-sm font-mono text-center text-blue-600 dark:text-blue-400 underline cursor-pointer select-none whitespace-nowrap overflow-hidden"
              style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }}
              onClick={(e) => { e.stopPropagation(); onOpenMAdjustment(relatedMAdjs[0].id); }}
            >
              {relatedMAdjs[0].jeNumber}
            </td>
          );
        }
        const isMAdjOpen = openMAdjPopoverAccountId === account.id;
        return (
          <td
            key={id}
            className="px-3 py-2 text-sm font-mono text-center select-none whitespace-nowrap overflow-visible"
            style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px`, position: 'relative' }}
            onClick={(e) => e.stopPropagation()}
          >
            <span
              className="text-blue-600 dark:text-blue-400 underline cursor-pointer"
              onClick={(e) => { e.stopPropagation(); setOpenMAdjPopoverAccountId(isMAdjOpen ? null : account.id!); }}
            >
              Multi
            </span>
            {isMAdjOpen && (
              <div
                ref={mAdjPopoverRef}
                className="absolute left-1/2 -translate-x-1/2 top-full mt-1 bg-white/95 dark:bg-neutral-800/95 backdrop-blur-sm border border-gray-200 dark:border-neutral-600 shadow-lg rounded-lg z-50 min-w-[140px] py-1"
              >
                {relatedMAdjs.map(adj => (
                  <div
                    key={adj.id}
                    className="px-3 py-1.5 text-sm font-mono text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-neutral-700 cursor-pointer whitespace-nowrap transition-colors"
                    onClick={(e) => { e.stopPropagation(); setOpenMAdjPopoverAccountId(null); onOpenMAdjustment(adj.id); }}
                  >
                    {adj.jeNumber}
                  </div>
                ))}
              </div>
            )}
          </td>
        );
      }
      case 'mAdj':
        return renderFinancialCell(row.id, id, rowIndex, account.mAdj, width);
      case 'ftax':
        return renderFinancialCell(row.id, id, rowIndex, account.ftax ?? 0, width, true);
      case 'grouping':
        return renderTextCell(row.id, id, rowIndex, account.grouping, width, false, 'text-gray-600 dark:text-gray-400');
      case 'subGrouping':
        return renderTextCell(row.id, id, rowIndex, account.subGrouping || '-', width, false, 'text-gray-600 dark:text-gray-400');
      case 'mAdjRef':
        return renderTextCell(row.id, id, rowIndex, account.mAdjRef || '\u2014', width, false, 'font-mono text-purple-700 dark:text-purple-300');
      case 'pyAdjBook':
        return renderFinancialCell(row.id, id, rowIndex, account.pyAdjBook, width);
      case 'pyMAdj':
        return renderFinancialCell(row.id, id, rowIndex, account.pyMAdj, width);
      case 'pyFtax':
        return renderFinancialCell(row.id, id, rowIndex, account.pyFtax, width);
      default:
        return <td key={id} style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }}></td>;
    }
  };

  // Render subtotal cell based on column configuration
  const renderSubtotalCell = (
    row: TableRow,
    column: ColumnConfig,
    subtotal: SubtotalData,
  ) => {
    const { id, width } = column;
    const style = { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` };
    
    switch (id) {
      case 'select':
        return <td key={id} className="px-3 py-2 text-center" style={style}></td>;
      case 'accountNumber':
        // This will be handled by the colspan cell
        return null;
      case 'accountName':
        // Skip - part of colspan
        return null;
      case 'bookBalance':
        return renderFinancialCell(row.id, id, -1, subtotal.bookBalance, width, false, true);
      case 'jeNumber':
        return <td key={id} className="px-3 py-2 text-sm font-mono text-center whitespace-nowrap" style={style}>-</td>;
      case 'rje':
        return renderFinancialCell(row.id, id, -1, subtotal.rje, width, false, true);
      case 'aje':
        return renderFinancialCell(row.id, id, -1, subtotal.aje, width, false, true);
      case 'adjBook':
        return renderFinancialCell(row.id, id, -1, subtotal.adjBook, width, false, true);
      case 'mAdjNumber':
        return <td key={id} className="px-3 py-2 text-sm font-mono text-center whitespace-nowrap" style={style}>-</td>;
      case 'mAdj':
        return renderFinancialCell(row.id, id, -1, subtotal.mAdj, width, false, true);
      case 'ftax':
        return renderFinancialCell(row.id, id, -1, subtotal.ftax, width, false, true);
      case 'grouping':
      case 'subGrouping':
      case 'mAdjRef':
        return <td key={id} className="px-3 py-2 text-sm whitespace-nowrap" style={style}>-</td>;
      case 'pyAdjBook':
        return renderFinancialCell(row.id, id, -1, subtotal.pyAdjBook, width, false, true);
      case 'pyMAdj':
        return renderFinancialCell(row.id, id, -1, subtotal.pyMAdj, width, false, true);
      case 'pyFtax':
        return renderFinancialCell(row.id, id, -1, subtotal.pyFtax, width, false, true);
      default:
        return <td key={id} className="px-3 py-2" style={style}></td>;
    }
  };
  
  const renderAccountCells = (row: TableRow, rowIndex: number) => {
    const account = row.data as TBAccount;
    return (
      <>
        {visibleColumns.map(col => renderCell(row, rowIndex, col, account))}
      </>
    );
  };
  
  const renderSubtotalCells = (row: TableRow) => {
    const subtotal = row.data as SubtotalData;
    const isExpanded = viewState.expandAll || expandedRows.has(row.id);

    // Determine which label column (accountNumber / accountName) appears first
    // in the current column order so the label cell is always anchored correctly
    // regardless of drag-and-drop reordering.
    const accountNumberIdx = visibleColumns.findIndex(c => c.id === 'accountNumber');
    const accountNameIdx = visibleColumns.findIndex(c => c.id === 'accountName');

    let firstLabelColId: ColumnId | null = null;
    let secondLabelColId: ColumnId | null = null;

    if (accountNumberIdx >= 0 && accountNameIdx >= 0) {
      if (accountNumberIdx < accountNameIdx) {
        firstLabelColId = 'accountNumber';
        secondLabelColId = 'accountName';
      } else {
        firstLabelColId = 'accountName';
        secondLabelColId = 'accountNumber';
      }
    } else if (accountNumberIdx >= 0) {
      firstLabelColId = 'accountNumber';
    } else if (accountNameIdx >= 0) {
      firstLabelColId = 'accountName';
    }

    const firstLabelIdx = firstLabelColId === 'accountNumber' ? accountNumberIdx : accountNameIdx;
    const secondLabelIdx = secondLabelColId === 'accountNumber' ? accountNumberIdx : accountNameIdx;
    // Only use colSpan=2 when both columns exist AND are adjacent; otherwise a
    // non-adjacent colSpan would absorb the wrong column.
    const adjacentLabels = secondLabelColId !== null && (secondLabelIdx - firstLabelIdx === 1);
    const labelColspan = adjacentLabels ? 2 : 1;
    const labelWidth = visibleColumns
      .filter(c => c.id === 'accountNumber' || c.id === 'accountName')
      .reduce((sum, c) => sum + c.width, 0);

    return (
      <>
        {visibleColumns.map((col) => {
          // Handle select column
          if (col.id === 'select') {
            const selectLeft = stickyOffsets['select'];
            return (
              <td
                key={col.id}
                className="px-3 py-2 text-center bg-white dark:bg-neutral-800 border-r border-gray-300 dark:border-neutral-600"
                style={{
                  width: `${col.width}px`, minWidth: `${col.width}px`, maxWidth: `${col.width}px`,
                  ...(selectLeft !== undefined ? { position: 'sticky' as const, left: `${selectLeft}px`, zIndex: 20 } : {})
                }}
              />
            );
          }

          // First label column: render the combined label cell
          if (firstLabelColId && col.id === firstLabelColId) {
            const labelLeft = stickyOffsets[firstLabelColId];
            return (
              <td
                key="subtotal-label"
                colSpan={labelColspan}
                className="px-3 py-2 text-sm font-bold text-taxtrail-text dark:text-taxtrail-dark-text uppercase cursor-pointer whitespace-nowrap overflow-hidden bg-white dark:bg-neutral-800"
                style={{
                  width: `${labelWidth}px`, minWidth: `${labelWidth}px`,
                  ...(labelLeft !== undefined ? { position: 'sticky' as const, left: `${labelLeft}px`, zIndex: 20 } : {})
                }}
                onClick={(e) => handleToggleRow(row.id, e)}
              >
                <div className="flex items-center" style={{ marginLeft: `${row.indent * 20}px` }}>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 mr-2 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 mr-2 flex-shrink-0" />
                  )}
                  <span className="truncate">{subtotal.label}</span>
                  <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400 flex-shrink-0">
                    ({subtotal.count})
                  </span>
                </div>
              </td>
            );
          }

          // Second label column: skip if adjacent (absorbed by colSpan), else empty cell
          if (secondLabelColId && col.id === secondLabelColId) {
            if (adjacentLabels) return null;
            return <td key={col.id} style={{ width: `${col.width}px`, minWidth: `${col.width}px`, maxWidth: `${col.width}px` }}></td>;
          }

          // Render other cells
          return renderSubtotalCell(row, col, subtotal);
        })}
      </>
    );
  };
  
  // Get visible columns
  const visibleColumns = columns.filter(col => col.visible);

  // Compute sticky left offsets for locked columns (updates on resize/reorder)
  const stickyOffsets = useMemo(() => {
    const offsets: Partial<Record<ColumnId, number>> = {};
    let left = 0;
    for (const col of columns) {
      if (!col.visible) continue;
      if (LOCKED_COLUMN_IDS.has(col.id)) {
        offsets[col.id] = left;
        left += col.width;
      }
    }
    return offsets;
  }, [columns]);

  // ID of the rightmost locked column in current visible order (receives the boundary border)
  const lastLockedColumnId = useMemo(() => {
    let lastId: ColumnId | null = null;
    for (const col of columns) {
      if (col.visible && LOCKED_COLUMN_IDS.has(col.id)) lastId = col.id;
    }
    return lastId;
  }, [columns]);

  // ============================================================================
  // VIRTUOSO COMPONENTS & RENDERERS
  // ============================================================================

  // Stable component overrides for TableVirtuoso.
  // NOTE: useMemo deps include selectedAccountIds / onRowClick / onRowDoubleClick
  // so that row selection highlighting and click handlers stay current.
  // This causes Virtuoso to reconcile (not unmount) rows when selection changes.
  const virtuosoComponents = useMemo((): TableComponents<TableRow> => ({
    Table: ({ style, ...props }: React.HTMLProps<HTMLTableElement>) => (
      <table
        {...props}
        style={{
          ...style,
          tableLayout: 'fixed',
          width: totalTableWidth,
          minWidth: totalTableWidth,
          borderCollapse: 'collapse',
        }}
      />
    ),
    Scroller: React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
      ({ style, ...props }, ref) => (
        <div {...props} ref={ref} style={{ ...style, overflow: 'auto' }} />
      )
    ),
    TableHead: React.forwardRef<HTMLTableSectionElement, React.HTMLProps<HTMLTableSectionElement>>(
      ({ style, ...props }, ref) => (
        <thead {...props} ref={ref} className="bg-gray-100 dark:bg-neutral-700" style={{ ...style, zIndex: 30 }} />
      )
    ),
    TableBody: React.forwardRef<HTMLTableSectionElement, React.HTMLProps<HTMLTableSectionElement>>(
      (props, ref) => <tbody {...props} ref={ref} />
    ),
    TableRow: ({ item, ...props }: { item: TableRow } & React.HTMLProps<HTMLTableRowElement>) => {
      if (item.isSubtotal) {
        const bgColorMap: Record<string, string> = {
          entity: 'bg-blue-100 dark:bg-blue-900/30',
          fsref: 'bg-purple-50 dark:bg-purple-900/20',
          grouping: 'bg-gray-100 dark:bg-neutral-700',
          subgrouping: 'bg-gray-50 dark:bg-slate-800',
          account: '',
        };
        const bgColor = bgColorMap[item.type] ?? 'bg-gray-100 dark:bg-neutral-700';
        return (
          <tr
            {...props}
            className={`${bgColor} border-b-2 border-gray-300 dark:border-neutral-600 font-semibold hover:bg-gray-200 dark:hover:bg-neutral-600 transition-colors`}
          />
        );
      }
      const account = item.data as TBAccount;
      const isSelected = selectedAccountIds.has(account.id!);
      return (
        <tr
          {...props}
          onClick={() => onRowClick(account)}
          onDoubleClick={() => onRowDoubleClick(account)}
          className={`border-b border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors ${
            isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
          }`}
        />
      );
    },
    EmptyPlaceholder: () => (
      <tr>
        <td colSpan={visibleColumns.length} className="h-64">
          <div className="flex flex-col items-center justify-center gap-3 h-full text-sm text-gray-500 dark:text-gray-400">
            {hasActiveFilters ? (
              <>
                <span>No accounts match the current filters.</span>
                <button
                  onClick={onReset}
                  className="px-3 py-1.5 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  Clear All Filters
                </button>
              </>
            ) : (
              <span>No accounts to display.</span>
            )}
          </div>
        </td>
      </tr>
    ),
  }), [totalTableWidth, selectedAccountIds, onRowClick, onRowDoubleClick, hasActiveFilters, onReset, visibleColumns.length]);

  // Renders the sticky <thead> rows (column headers + optional filter row)
  const fixedHeaderContent = useCallback(() => (
    <>
      <tr className="border-b-2 border-gray-300 dark:border-neutral-600">
        {visibleColumns.map((col, idx) => (
          <ColumnHeader
            key={col.id}
            column={col}
            index={idx}
            isResizing={resizeState.isResizing && resizeState.columnId === col.id}
            isDragging={dragState.isDragging && dragState.columnId === col.id}
            isDropTarget={dragState.isDragging && dragState.dropTargetIndex === idx && dragState.columnId !== col.id}
            onResizeStart={handleResizeStart}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDrop={handleDrop}
            masterCheckboxState={col.id === 'select' ? masterCheckboxState : undefined}
            onMasterCheckboxClick={col.id === 'select' ? handleMasterCheckboxClick : undefined}
            stickyLeft={stickyOffsets[col.id]}
            isLastLocked={col.id === lastLockedColumnId}
          />
        ))}
      </tr>
      {showFilters && (
        <FilterRow
          filters={filters}
          onFilterChange={handleFilterChange}
          onMatchTypeChange={handleMatchTypeChange}
          onClearFilters={handleClearFilters}
          uniqueGroupings={uniqueGroupings}
          hasActiveFilters={hasActiveFilters}
          columns={columns}
          stickyOffsets={stickyOffsets}
          lastLockedColumnId={lastLockedColumnId}
        />
      )}
    </>
  ), [visibleColumns, resizeState, dragState, showFilters, filters, columns,
      masterCheckboxState, uniqueGroupings, hasActiveFilters, stickyOffsets, lastLockedColumnId,
      handleResizeStart, handleDragStart, handleDragOver, handleDragEnd, handleDrop,
      handleMasterCheckboxClick, handleFilterChange, handleMatchTypeChange,
      handleClearFilters]);

  // Renders the <td> cells for each row (no <tr> wrapper — that's in components.TableRow)
  const itemContent = useCallback((_index: number, row: TableRow) => {
    if (row.isSubtotal) {
      return renderSubtotalCells(row);
    }
    return renderAccountCells(row, _index);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumns, visibleRows, expandedRows, viewState, cellSelection.isDragging,
      isCellSelected, handleCellClick, handleCellMouseDown, handleCellMouseEnter,
      handleToggleRow, handleCheckboxClick, selectedAccountIds, mAdjHeaders,
      onUpdateMAdjRef, openPopoverAccountId, onOpenJournalEntry]);

  return (
    <div className="h-full flex flex-col" ref={tableContainerRef}>
      {/* Consolidated toolbar: viewState controls + filter toggle */}
      <div className="flex-shrink-0 px-4 py-2 bg-gray-50 dark:bg-neutral-800 border-b border-gray-200 dark:border-neutral-700 flex items-center gap-2 overflow-hidden whitespace-nowrap">
        {/* Expand/Collapse */}
        <button
          onClick={onExpandCollapse}
          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
            viewState.expandAll
              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-600 dark:border-green-500'
              : 'text-gray-700 dark:text-gray-200 bg-white dark:bg-neutral-700 border border-gray-300 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-600'
          }`}
          title={viewState.expandAll ? 'Click to collapse all' : 'Click to expand all'}
        >
          {viewState.expandAll ? 'Collapse All' : 'Expand All'}
        </button>
        {entityCount > 1 && (
          <>
            <div className="w-px h-5 bg-gray-300 dark:bg-neutral-600" />
            {/* Entities */}
            <button
              onClick={onToggleEntities}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                viewState.showEntities
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-600 dark:border-blue-500'
                  : 'text-gray-700 dark:text-gray-200 bg-white dark:bg-neutral-700 border border-gray-300 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-600'
              }`}
              title="Toggle entity subtotals"
            >
              Entities
            </button>
          </>
        )}
        {/* FS Ref */}
        <button
          onClick={onToggleFSRef}
          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
            viewState.showFSRef
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-600 dark:border-blue-500'
              : 'text-gray-700 dark:text-gray-200 bg-white dark:bg-neutral-700 border border-gray-300 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-600'
          }`}
          title="Toggle FS reference subtotals"
        >
          FS Ref
        </button>
        {/* Groupings */}
        <button
          onClick={onToggleGroupings}
          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
            viewState.showGroupings
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-600 dark:border-blue-500'
              : 'text-gray-700 dark:text-gray-200 bg-white dark:bg-neutral-700 border border-gray-300 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-600'
          }`}
          title="Toggle grouping subtotals"
        >
          Groupings
        </button>
        {/* SubGroupings */}
        <button
          onClick={onToggleSubGroupings}
          className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
            viewState.showSubGroupings
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-600 dark:border-blue-500'
              : 'text-gray-700 dark:text-gray-200 bg-white dark:bg-neutral-700 border border-gray-300 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-600'
          }`}
          title="Toggle subgrouping subtotals"
        >
          SubGroupings
        </button>
        <div className="w-px h-5 bg-gray-300 dark:bg-neutral-600" />
        {/* Reset */}
        <button
          onClick={onReset}
          className="p-1.5 text-gray-600 dark:text-gray-300 bg-white dark:bg-neutral-700 border border-gray-300 dark:border-neutral-600 rounded hover:bg-gray-50 dark:hover:bg-neutral-600 transition-colors"
          title="Reset to default view"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-gray-300 dark:bg-neutral-600" />
        {/* Filter toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-1.5 p-1.5 rounded transition-colors ${
            showFilters || hasActiveFilters
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-500'
              : 'bg-white dark:bg-neutral-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-600'
          }`}
          title={showFilters ? 'Hide filters' : 'Show filters'}
        >
          <Filter className="w-4 h-4" />
          {hasActiveFilters && (
            <span className="px-1.5 py-0.5 text-xs bg-green-600 text-white rounded-full">Active</span>
          )}
        </button>
        {/* Status info + Edit Columns */}
        <div className="ml-auto flex items-center gap-4 flex-shrink-0">
          {hasActiveFilters && (
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Showing {filteredAccounts.length} of {accounts.length} accounts
            </span>
          )}
          {cellSelection.selectedCells.size > 0 && (
            <span className="text-sm text-blue-600 dark:text-blue-400">
              {cellSelection.selectedCells.size} cell{cellSelection.selectedCells.size !== 1 ? 's' : ''} selected
              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(Ctrl+C to copy)</span>
            </span>
          )}
          {selectedAccountIds.size === 0 && (
            <button
              onClick={() => {
                const next = !isColumnPanelOpen;
                setIsColumnPanelOpen(next);
                onColumnPanelOpenChange?.(next);
              }}
              title={isColumnPanelOpen ? 'Close column settings' : 'Edit columns'}
              className={`p-1.5 transition-colors ${
                isColumnPanelOpen
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Virtualized table area - react-virtuoso handles both axes of scroll */}
      <TableVirtuoso<TableRow>
        style={{ flex: 1 }}
        data={visibleRows}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={itemContent}
        components={virtuosoComponents}
      />

      {/* ================================================================== */}
      {/* NET INCOME FOOTER (Sprint 2.10)                                    */}
      {/* Pinned to the bottom of the table card, OUTSIDE horizontal scroll  */}
      {/* so the user always sees the tie-out values.                        */}
      {/* ================================================================== */}
      <NetIncomeFooter
        netIncome={netIncome}
        selectionCount={selectionCount}
        selectionSum={selectionSum}
      />

      {isColumnPanelOpen && (
        <ColumnVisibilityPanel
          columns={columns}
          onToggleVisibility={handleToggleVisibility}
          onClose={() => {
            setIsColumnPanelOpen(false);
            onColumnPanelOpenChange?.(false);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Select a rectangular range of cells between two positions
 * Updated for Sprint 2.9 to use dynamic column order
 */
function selectCellRange(
  anchor: CellPosition,
  current: CellPosition,
  visibleRows: TableRow[],
  columns: ColumnConfig[]
): Set<string> {
  const selected = new Set<string>();
  
  // Find row indices
  const anchorRowIndex = visibleRows.findIndex(r => r.id === anchor.rowId);
  const currentRowIndex = visibleRows.findIndex(r => r.id === current.rowId);
  
  if (anchorRowIndex === -1 || currentRowIndex === -1) return selected;
  
  // Find column indices based on current column order
  const visibleCols = columns.filter(c => c.visible);
  const anchorColIndex = visibleCols.findIndex(c => c.id === anchor.columnId);
  const currentColIndex = visibleCols.findIndex(c => c.id === current.columnId);
  
  if (anchorColIndex === -1 || currentColIndex === -1) return selected;
  
  // Calculate bounds
  const minRow = Math.min(anchorRowIndex, currentRowIndex);
  const maxRow = Math.max(anchorRowIndex, currentRowIndex);
  const minCol = Math.min(anchorColIndex, currentColIndex);
  const maxCol = Math.max(anchorColIndex, currentColIndex);
  
  // Select all cells in the rectangular range (excluding subtotals and select column)
  for (let rowIdx = minRow; rowIdx <= maxRow; rowIdx++) {
    const row = visibleRows[rowIdx];
    if (row.isSubtotal) continue;
    
    for (let colIdx = minCol; colIdx <= maxCol; colIdx++) {
      const column = visibleCols[colIdx];
      if (column.id === 'select') continue; // Don't select the checkbox column
      selected.add(createCellKey(row.id, column.id));
    }
  }
  
  return selected;
}

/**
 * Get numeric value for a cell (for sum calculation)
 */
function getNumericValue(account: TBAccount, columnId: string): number | null {
  switch (columnId) {
    case 'bookBalance': return account.bookBalance;
    case 'rje': return account.rje;
    case 'aje': return account.aje;
    case 'adjBook': return account.adjBook ?? 0;
    case 'mAdj': return account.mAdj;
    case 'ftax': return account.ftax ?? 0;
    case 'pyAdjBook': return account.pyAdjBook;
    case 'pyMAdj': return account.pyMAdj;
    case 'pyFtax': return account.pyFtax;
    default: return null;
  }
}

/**
 * Sanitize text for spreadsheet (prevent formula injection)
 */
function sanitizeForSpreadsheet(value: string): string {
  if (typeof value !== 'string') return String(value);
  
  const dangerousChars = ['=', '+', '-', '@'];
  const firstChar = value.trim().charAt(0);
  
  if (dangerousChars.includes(firstChar)) {
    return `'${value}`;
  }
  
  return value;
}

/**
 * Get display value for clipboard copy
 */
function getCellDisplayValueForCopy(account: TBAccount, columnId: ColumnId): string {
  switch (columnId) {
    case 'accountNumber':
      return sanitizeForSpreadsheet(account.accountNumber);
    case 'accountName':
      return sanitizeForSpreadsheet(account.accountName);
    case 'bookBalance':
      return displayAmountRounded(account.bookBalance).text;
    case 'jeNumber':
      return sanitizeForSpreadsheet(account.jeNumber || '-');
    case 'rje':
      return displayAmountRounded(account.rje).text;
    case 'aje':
      return displayAmountRounded(account.aje).text;
    case 'adjBook':
      return displayAmountRounded(account.adjBook ?? 0).text;
    case 'mAdjNumber':
      return sanitizeForSpreadsheet(account.mAdjNumber || '-');
    case 'mAdj':
      return displayAmountRounded(account.mAdj).text;
    case 'ftax':
      return displayAmountRounded(account.ftax ?? 0).text;
    case 'grouping':
      return sanitizeForSpreadsheet(account.grouping);
    case 'subGrouping':
      return sanitizeForSpreadsheet(account.subGrouping || '-');
    case 'mAdjRef':
      return '-';
    case 'pyAdjBook':
      return displayAmountRounded(account.pyAdjBook).text;
    case 'pyMAdj':
      return displayAmountRounded(account.pyMAdj).text;
    case 'pyFtax':
      return displayAmountRounded(account.pyFtax).text;
    default:
      return '';
  }
}

